import React, { Suspense, lazy } from "react"
import ReactDOM from "react-dom/client"
import { PersistGate } from "redux-persist/integration/react"
import { Provider } from "react-redux"
import * as Sentry from "@sentry/react"

import { config } from "Utils/config"
import { store, persistor } from "./store"
import ApolloProviderWrapper from "./ApolloProviderWrapper.provider"
import WebSocketLostModal from "Shared/components/WebSocketLostModal"
import ApiProviderWrapper from "./ApiProviderWrapper"
import ResilientErrorBoundary from "Shared/components/ResilientErrorBoundary"
import { VersionBanner } from "Shared/version/VersionBanner"
import BusinessThemeProvider from "Shared/components/BusinessThemeProvider"
import SplashScreen from "./components/SplashScreen"
// Polices du produit, servies par l'application elle-même (plus de Google Fonts).
import "./styles/fonts.css"
// import { SpeechRecognitionProvider } from "Shared/context/SpeechRecognitionContext"
// import UserChangeDetector from "Shared/components/UserChangeDetector"

const App = lazy(() => import("Containers/App"))
const FeedbackDialogHost = lazy(() =>
  import("Shared/components/FeedbackDialog/FeedbackDialogHost").then(m => ({
    default: m.FeedbackDialogHost,
  }))
)

/* ================= Sentry v8 ================= */

const truthy = <T,>(arr: T[]): T[] => arr.filter(Boolean) as T[]
const uniq = <T,>(arr: T[]) => Array.from(new Set(arr))

if (config.SENTRY_DSN) {
  const enableReplay = (config as any).SENTRY_REPLAY !== false
  const env = String(config.ENVIRONMENT || "local").toLowerCase()
  const isLocal = env === "local"
  const isProd = env === "prod" || env === "production"

  const traceTargets = uniq(
    truthy([
      config.APOLLO_SERVER_URL && `${config.APOLLO_SERVER_URL}/graphql`,
      config.FRONT_STUDIO_URL,
      config.API_URL,
      config.FORM_BACK_URL,
      isLocal && "http://localhost:5253",
    ])
  )

  Sentry.init({
    dsn: config.SENTRY_DSN,
    release: config.RELEASE,
    environment: config.ENVIRONMENT || "local",

    tracesSampler: () => (isProd ? 0.2 : 1.0),
    profilesSampleRate: isProd ? 0.1 : 1.0,
    replaysSessionSampleRate: enableReplay ? (isProd ? 0.05 : 0.2) : 0.0,
    replaysOnErrorSampleRate: enableReplay ? 1.0 : 0.0,

    integrations: truthy([
      Sentry.browserTracingIntegration({
        instrumentPageLoad: true,
        tracePropagationTargets: traceTargets,
      }),
      Sentry.browserProfilingIntegration?.(),
      enableReplay &&
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
          networkDetailAllowUrls: traceTargets,
          stickySession: true,
        }),
    ]),

    sendDefaultPii: false,
    maxBreadcrumbs: 100,
    normalizeDepth: 5,
    autoSessionTracking: true,
    debug: isLocal,

    beforeSend(event, hint) {
      const ua = window?.navigator?.userAgent || ""
      if (/HeadlessChrome|bot|crawl|spider/i.test(ua)) return null

      const err = hint?.originalException as any
      const gqlErr = err?.graphQLErrors?.[0] || err?.networkError
      if (gqlErr) {
        const op =
          err?.operationName || gqlErr?.operationName || (gqlErr?.path?.join?.(".") ?? "<GraphQL>")
        event.transaction = op
        event.fingerprint = ["{{ default }}", op, gqlErr?.extensions?.code ?? "GQL"]
      }

      if (event?.request?.headers) {
        delete (event.request.headers as any).authorization
        delete (event.request.headers as any).cookie
        delete (event.request.headers as any)["x-api-key"]
      }
      if (event?.user) {
        event.user = event.user.id ? { id: String(event.user.id) } : undefined
      }
      return event
    },

    beforeBreadcrumb(bc) {
      if (bc?.category === "console" && bc?.level === "log") return null
      return bc
    },
  })

  Sentry.setTags({ app: "pocketstudio-front" })
  try {
    const uid: unknown = (store.getState() as any)?.login?.user?.id
    if (uid != null) Sentry.setUser({ id: String(uid) })
  } catch {
    /* noop */
  }
}

/* ================= Boot ================= */
const renderApp = (keycloakLoginResponse: unknown = null) => {
  const container = document.getElementById("app") as HTMLElement
  const root = ReactDOM.createRoot(container)

  root.render(
    <Provider store={store}>
      <PersistGate persistor={persistor} loading={<SplashScreen />}>
        <ApolloProviderWrapper>
          <BusinessThemeProvider>
            <ApiProviderWrapper>
              <Suspense fallback={<SplashScreen />}>
                <ResilientErrorBoundary>
                  <WebSocketLostModal />
                  <VersionBanner />
                  {/* <UserChangeDetector /> */}
                  {/* <SpeechRecognitionProvider> */}
                  <App keycloakLoginResponse={keycloakLoginResponse as any} />
                  {/* </SpeechRecognitionProvider> */}
                </ResilientErrorBoundary>
                {config.SENTRY_DSN && (
                  <Suspense fallback={null}>
                    <FeedbackDialogHost />
                  </Suspense>
                )}
              </Suspense>
            </ApiProviderWrapper>
          </BusinessThemeProvider>
        </ApolloProviderWrapper>
      </PersistGate>
    </Provider>
  )
}

const initApp = () => {
  const pathname = window.location.pathname
  const hasClientOrRealm = pathname.includes("client")

  if (hasClientOrRealm) {
    import("Services/KeycloakService").then(m => (m.default as any).getKeycloakClient(renderApp))
  } else {
    renderApp()
  }
}

initApp()
